import React, { useCallback, useEffect, useState } from 'react';
import { apiGet } from '@/context/AuthContext';
import { ChevronLeft, ChevronRight, Disc3, Image, Pause, Play, RefreshCw, Info } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;

export default function SlideshowPage({ publicMode = false }) {
  const [uploads, setUploads] = useState([]);
  const [index, setIndex] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [paused, setPaused] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [transition, setTransition] = useState('fade');
  const [music, setMusic] = useState(null);

  const load = useCallback(() => {
    apiGet(publicMode ? '/api/uploads/public-slideshow' : '/api/uploads')
      .then((r) => {
        const images = (r.data.uploads || []).filter((u) =>
          u.category === 'event' &&
          u.approved === true &&
          !u.hidden &&
          (u.content_type || '').startsWith('image/')
        );
        setUploads(images);
        setLastUpdate(new Date());
        setIndex((current) => (images.length ? current % images.length : 0));
      })
      .catch(() => {});
  }, [publicMode]);

  useEffect(() => {
    load();
    const refresh = setInterval(load, 10000);
    return () => clearInterval(refresh);
  }, [load]);

  useEffect(() => {
    const loadMusic = () => {
      apiGet('/api/live')
        .then((response) => setMusic(response.data.music || null))
        .catch(() => {});
    };
    loadMusic();
    const refresh = setInterval(loadMusic, 5000);
    return () => clearInterval(refresh);
  }, []);

  useEffect(() => {
    if (uploads.length <= 1 || paused) return undefined;
    const advance = setInterval(() => {
      setIndex((current) => (current + 1) % uploads.length);
    }, 6000);
    return () => clearInterval(advance);
  }, [uploads.length, paused]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ') setPaused((current) => !current);
      if (e.key === 'ArrowRight') setIndex((current) => uploads.length ? (current + 1) % uploads.length : 0);
      if (e.key === 'ArrowLeft') setIndex((current) => uploads.length ? (current - 1 + uploads.length) % uploads.length : 0);
      if (e.key.toLowerCase() === 'i') setShowOverlay((current) => !current);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [uploads.length]);

  const current = uploads[index];
  const nowPlaying = music?.now_playing?.is_playing ? music.now_playing.track : null;
  const nextInQueue = music?.next_in_queue || null;
  const hasMusicOverlay = !!(nowPlaying || nextInQueue);

  return (
    <div className="h-screen w-screen bg-[#020617] text-slate-100 overflow-hidden select-none" data-testid="slideshow-page">
      {!current ? (
        <div className="h-full flex flex-col items-center justify-center px-6 text-center">
          <Image className="w-14 h-14 text-slate-600 mb-4" />
          <h1 className="text-2xl font-semibold text-slate-200 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Molin30-diasshow</h1>
          <p className="text-sm text-slate-500">Ingen uploadede billeder endnu.</p>
        </div>
      ) : (
        <SlideImage upload={current} showOverlay={showOverlay} transition={transition} musicOverlay={hasMusicOverlay} />
      )}

      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-slate-950/80 to-transparent">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Molin30" className="h-7" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Diasshow</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <RefreshCw className="w-3.5 h-3.5 text-blue-400" />
          <span>{uploads.length} billeder</span>
          <span className="text-slate-600">/</span>
          <span>{lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
        </div>
      </div>

      <div className={`absolute right-6 ${hasMusicOverlay ? 'bottom-28' : 'bottom-5'} flex items-center gap-2 z-20 transition-[bottom]`}>
        <button onClick={() => setIndex((current) => uploads.length ? (current - 1 + uploads.length) % uploads.length : 0)} className="p-2 rounded-full bg-slate-900/70 border border-slate-700 text-slate-300 hover:text-white" title="Forrige">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button onClick={() => setPaused((current) => !current)} className="p-2 rounded-full bg-slate-900/70 border border-slate-700 text-slate-300 hover:text-white" title={paused ? 'Afspil' : 'Sæt på pause'}>
          {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
        </button>
        <button onClick={() => setIndex((current) => uploads.length ? (current + 1) % uploads.length : 0)} className="p-2 rounded-full bg-slate-900/70 border border-slate-700 text-slate-300 hover:text-white" title="Næste">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button onClick={() => setShowOverlay((current) => !current)} className="p-2 rounded-full bg-slate-900/70 border border-slate-700 text-slate-300 hover:text-white" title="Toggle overlay">
          <Info className="w-4 h-4" />
        </button>
        <select value={transition} onChange={(e) => setTransition(e.target.value)} className="bg-slate-900/70 border border-slate-700 rounded-full px-3 py-2 text-xs text-slate-300">
          <option value="fade">Fade</option>
          <option value="zoom">Zoom</option>
          <option value="plain">Plain</option>
        </select>
      </div>

      {uploads.length > 1 && (
        <div className={`absolute ${hasMusicOverlay ? 'bottom-28' : 'bottom-5'} left-1/2 -translate-x-1/2 flex items-center gap-2 z-10 transition-[bottom]`}>
          {uploads.slice(0, 12).map((upload, dotIndex) => (
            <span
              key={upload.id}
              className={`h-1.5 rounded-full transition-all ${dotIndex === index ? 'w-8 bg-blue-400' : 'w-1.5 bg-slate-600'}`}
            />
          ))}
        </div>
      )}

      {hasMusicOverlay && (
        <MusicOverlay nowPlaying={nowPlaying} nextInQueue={nextInQueue} />
      )}
    </div>
  );
}

function MusicOverlay({ nowPlaying, nextInQueue }) {
  return (
    <div className="absolute inset-x-0 bottom-0 z-20 border-t border-slate-700/70 bg-slate-950/95 px-6 py-4 shadow-[0_-12px_40px_rgba(2,6,23,0.65)] backdrop-blur-md" data-testid="slideshow-music-overlay">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 sm:grid-cols-2">
        <MusicItem
          label="Nu spiller"
          track={nowPlaying}
          emptyText="Ingen musik afspilles"
          accent="text-emerald-300"
        />
        {nextInQueue && (
          <MusicItem
            label="Næste i kø"
            track={nextInQueue}
            accent="text-blue-300"
          />
        )}
      </div>
    </div>
  );
}

function MusicItem({ label, track, emptyText, accent }) {
  const title = track?.name || track?.spotify_name || track?.song_title;
  const artist = track?.artist || track?.spotify_artist;
  const albumArt = track?.album_art || track?.spotify_album_art;

  return (
    <div className="flex min-w-0 items-center gap-4">
      {albumArt ? (
        <img src={albumArt} alt="" className="h-14 w-14 flex-shrink-0 rounded-md object-cover" />
      ) : (
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-slate-800">
          <Disc3 className="h-6 w-6 text-slate-500" />
        </div>
      )}
      <div className="min-w-0">
        <p className={`text-xs font-bold uppercase tracking-[0.18em] ${accent}`}>{label}</p>
        {title ? (
          <>
            <p className="mt-1 truncate text-lg font-semibold text-white">{title}</p>
            {artist && <p className="truncate text-sm text-slate-300">{artist}</p>}
          </>
        ) : (
          <p className="mt-1 text-sm text-slate-500">{emptyText}</p>
        )}
      </div>
    </div>
  );
}

function SlideImage({ upload, showOverlay, transition, musicOverlay }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!upload?.storage_path) return undefined;
    let objectUrl = null;
    let active = true;
    const token = localStorage.getItem('token');
    setSrc(null);

    fetch(`${API}/api/files/${upload.storage_path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.ok) return r.blob();
        throw new Error('Billedet kunne ikke indlæses');
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc(null);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [upload?.storage_path]);

  return (
    <div className="h-full w-full flex items-center justify-center">
      {src ? (
        <img
          src={src}
          alt={upload.original_filename || 'Uploadet eventbillede'}
          className={`h-full w-full object-contain ${transition === 'fade' ? 'animate-fade-in' : ''} ${transition === 'zoom' ? 'scale-[1.01] transition-transform duration-1000' : ''}`}
        />
      ) : (
        <div className="flex flex-col items-center justify-center text-slate-500">
          <Image className="w-12 h-12 mb-3" />
          <p className="text-sm">Billedet indlæses...</p>
        </div>
      )}
      {showOverlay && (
        <div className={`absolute ${musicOverlay ? 'bottom-24' : 'bottom-0'} left-0 right-0 px-6 py-5 bg-gradient-to-t from-slate-950/80 to-transparent`}>
          <p className="text-sm text-slate-300">{upload.guest_name || 'Molin30 guest'}</p>
          <p className="text-xs text-slate-500">{upload.created_at ? new Date(upload.created_at).toLocaleString() : ''}</p>
        </div>
      )}
    </div>
  );
}
